<template>
  <b-container class="p-0 max-vh-100 d-flex flex-column flex-fill message-edit-container" fluid>
    <!-- アップロード中のローディング表示 -->
    <loading-upload v-if="isUploading && !isModal" :msg="loadingMsg" />

    <navigation-bar
      v-if="nav"
      :items="items"
      :cancel="true"
      @on-cancel="showEditDiscardAlert(onCancel)"
      @on-complete="onEditComplete"
      :title="navTitle"
      :fetchUserInfo="false"
    />

    <b-container
      class="d-flex flex-column flex-fill message-edit-body"
      :class="{ 'my-3': !isModal }"
      :style="isModal ? 'max-height: 75vh;' : ''"
      @dragover.prevent="drag = true"
      @dragleave.prevent="drag = false"
      v-on:drop.prevent="drag = false"
    >
      <drag-area-overlay :drag="drag" v-on:dropFiles="dropFiles" />

      <b-row class="d-flex">
        <b-col lg="6" xl="6">
          <!-- タイトル入力 -->
          <b-form-group
            id="message-title-label"
            label="タイトル"
            label-for="message-title-input"
            label-align="left"
          >
            <b-form-input
              id="message-title-input"
              input="text"
              class="title-input"
              v-model="titleSource"
              placeholder="タイトルを入力してください"
              aria-describedby="title-feedback"
              autofocus
              required
              @update="changeEditMode(editMode)"
            />
            <b-form-text id="title-feedback" :class="titleState ? 'success' : 'warning'">
              {{ validTitleMessage }}
            </b-form-text>
          </b-form-group>

          <!-- 本文の入力 -->
          <b-form-group
            id="message-content-label"
            label="内容"
            label-for="message-content-input"
            label-align="left"
          >
            <b-form-textarea
              id="message-content-input"
              class="content-input"
              rows="7"
              v-model="messageSource"
              placeholder="内容を入力してください"
              aria-describedby="content-feedback"
              @update="changeEditMode(editMode)"
            />
            <b-form-text id="content-feedback" :class="contentState ? 'success' : 'warning'">
              {{ validContentMessage }}
            </b-form-text>
          </b-form-group>
        </b-col>

        <b-col lg="6" xl="6">
          <!-- 添付ファイルフォーム -->
          <file-selection-form
            ref="fileSelectionForm"
            contentType="MESSAGE"
            label="ファイル"
            :multiple="true"
            :photos="photos"
            :messageId="this.normalizeMessageId"
            :allow_attachment_type="allow_attachment_type"
            :file-number-limit="10"
            v-on:onUpdateThumb="updateFile"
          />

          <!-- 作成者以外の書込みを禁止する -->
          <b-row class="d-flex flex-column" v-if="hasFeature('deny-edit')" no-gutters>
            <b-form-group
              label-class="d-flex"
              class="d-flex"
              label="作成者以外のコメント・いいね"
              v-slot="{ ariaDescribedby }"
            >
              <b-form-radio-group
                v-model="allowWriteAndLikeOtherSource"
                :options="radioOptions"
                :aria-describedby="ariaDescribedby"
                style="display: flex; flex-direction: column"
                :disabled="!topicAllowWriteOthersSource"
                @change="changeEditMode(editMode)"
              >
                <b-form-text v-if="!topicAllowWriteOthersSource" class="warning mt-0"
                  >話題で作成者以外の書き込みが許可されていません</b-form-text
                >
              </b-form-radio-group>
            </b-form-group>
          </b-row>

          <!-- トークへメッセージ送信設定 -->
          <b-form-group label="トークへメッセージ送信設定">
            <!-- トーク選択 -->
            <talk-select-modal
              id="talk-select-modal"
              :talks="talks"
              :default-talk-id="talkIdSource"
              @selected="talkSelected"
            />

            <!-- 所属していないトーク変更時の確認 -->
            <alert-modal
              id="confirm-not-joined-talk"
              :msg="'所属していないトークが選択されています。\n変更すると元に戻せません。\n変更しますか？'"
              okLabel="変更"
              @clickedOK="confirmedNotJoinedTalk"
            />

            <!-- トーク選択ボタン -->
            <b-button class="talk-select-button mb-1" @click="showTalkSelectModal">
              <span class="open-icon"></span>
              <span v-if="showOpenTalkIcon" class="open-talk-icon">
                <img src="../assets/icon_openchat.svg" />
              </span>
              <span :class="`talk-select-button-label ${talkIdSource ? 'selected' : ''}`">
                {{ talkSelectLabel }}
              </span>
            </b-button>

            <b-form-checkbox
              v-model="sendTalkOnCreateSource"
              :disabled="sendTalkOnCreateDisabled"
              @change="changeEditMode(editMode)"
            >
              作成時に送信
            </b-form-checkbox>

            <b-form-checkbox
              v-model="sendTalkOnUpdateSource"
              :disabled="sendTalkOnUpdateDisabled"
              @change="changeEditMode(editMode)"
            >
              編集時に送信
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
    </b-container>

    <!-- フッター -->
    <b-row v-if="isModal" class="mt-2">
      <b-col class="d-flex justify-content-between ml-auto" lg="6" xl="6">
        <cancel class="col-5" label="キャンセル" v-on:cancel="showEditDiscardAlert(onCancel)" />
        <confirm
          class="col-5"
          label="保存"
          :disabled="confirmButtonDisabled"
          v-on:confirm="onEditComplete"
        />
      </b-col>
    </b-row>
  </b-container>
</template>

<style lang="scss" scoped>
.message-edit-container {
  text-align: left;
  min-height: 0;

  .message-edit-body {
    overflow-y: auto !important;
    min-height: 0;
  }
}

/* file-list-viewer の m-2 を打ち消すのと overflow 設定 */
.file-list-viewer-style {
  min-height: 60px; /* 必ず1枚分の高さを確保する */
  overflow: auto;
  flex-wrap: nowrap;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.title-input {
  background-color: #f3f3f3;
  border-radius: 3px;
  font-size: 16px;
}

.attachment-link {
  display: flex;
  flex-direction: row;
  justify-items: center;
  margin: 4px;
}

.paperclip {
  color: #000000;
}

.attachement-label {
  display: flex;
  color: #00b7c6;
  font-size: 14px;
  cursor: pointer;
}

::placeholder {
  color: #bbbbbb;
}

.content-input {
  background-color: #f3f3f3;
  font-size: 16px;
}

.not-allowed {
  color: gray;
  cursor: not-allowed;
}

.number-limit-label {
  margin-left: 8px;
  color: #00b7c6;
  font-size: 14px;
}

.talk-select-button {
  display: flex;
  align-items: center;
  width: 100%;
  color: #212529;
  background-color: transparent;
  border-color: #ced4da;
  gap: 0.25rem;

  &:hover,
  &:active,
  &:focus,
  &:focus-visible {
    color: #212529 !important;
    background-color: transparent !important;
    border: 1px solid #ced4da !important;
    box-shadow: none !important;
  }

  .open-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    background-image: url("../assets/open.svg");
  }

  .open-talk-icon {
    display: flex;
    align-items: center;
  }

  .talk-select-button-label {
    color: #797979;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.selected {
      color: #212529;
    }
  }
}
</style>

<!-- disabled なチェックボックスのスタイル -->
<style>
#allow-write-others[disabled] ~ .custom-control-label {
  color: #cf005880;
}
#allow-write-others[disabled] ~ .custom-control-label::before {
  background-color: #cf005840;
  border-color: #cf005880;
}
</style>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useAppStore, useDomainStore, useMessageStore, useTopicStore } from "@/store";
import NavigationBar from "./NavigationBar.vue";
import type { Attachment } from "../model";
import { AttachmentFactory, Message, Acl } from "../model";
import { v4 as uuidv4 } from "uuid";
import Confirm from "./button/Confirm.vue";
import Cancel from "./button/Cancel.vue";
import { useTextProcess } from "@/composables/useTextProcess";
import AlertModal from "./AlertModal.vue";
import { Route, NavigationGuardNext } from "vue-router";
import { useEditDiscard } from "@/composables/useEditDiscard";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";
import LoadingUpload from "./loading/LoadingUpload.vue";
import DragAreaOverlay from "./DropAreaOverlay.vue";
import AclManager from "../model/acl-manager";
import { allowCreateMessage, allowFeature, FeatureName } from "../direct-app-config";
import FileSelectionForm from "./FileSelectionForm.vue";
import type { AttachmentFileTypes } from "@/suppport-attachment-types";
import { AttachmentFileTypesDefault, AttachmentFileTypesNone } from "@/suppport-attachment-types";
import { Topic } from "../model";
import S3AccessUtility from "./s3-acceess-utility";
import TalkSelectModal from "./talk-select/TalkSelectModal.vue";
import { Talk } from "@/model/talk";

// const MESSAGE_TITLE_MAX_LENGTH = 100;  // タイトル文字数100文字まで → titleMaxLength に移動
const MESSAGE_CONTENT_MAX_LENGTH = 5000;

type PrevData = {
  title: string;
  message: string;
  files: (File | Attachment)[];
  acl: Acl;
};

export default defineComponent({
  name: "message-edit",
  components: {
    NavigationBar,
    Confirm,
    Cancel,
    AlertModal,
    LoadingUpload,
    DragAreaOverlay,
    FileSelectionForm,
    TalkSelectModal,
  },
  props: {
    nav: { type: Boolean, default: () => false },

    /** create: 新規作成 edit: 編集 */
    type: { type: String, default: () => "edit" },

    domainId: { type: String, default: () => "" }, //!< 所属組織ID
    topicId: { type: String, default: () => "" }, //!< 所属話題ID
    messageId: { type: String, default: () => "" }, //!< 投稿ID

    /** タイトル */
    title: { type: String, default: () => "" },

    /** 内容 */
    message: { type: String, default: () => "" },

    /** ファイル */
    photos: { type: Array as PropType<Attachment[]>, default: () => [] },

    isModal: { type: Boolean, default: () => false },

    // ファイル添付設定
    allow_attachment_type: {
      default: () => AttachmentFileTypesDefault as AttachmentFileTypes,
    },

    // ACL
    acl: { type: Object as PropType<Acl>, default: () => Acl.createDummy() },
    topicAcl: { type: Object as PropType<Acl>, default: () => Acl.createDummy() },

    // トークID
    talkId: { type: String, default: () => "" },

    // 作成時に送信
    sendTalkOnCreate: { type: Boolean, default: () => false },

    // 編集時に送信
    sendTalkOnUpdate: { type: Boolean, default: () => false },
  },
  setup(props, { emit }) {
    // 旧 TextProcessMixin / EditDiscardMixin → composable（1-D 利用側書き換え）
    const { getLength } = useTextProcess();
    const { changeEditMode, showEditDiscardAlert, routeLeaveEvent } = useEditDiscard();
    const rootEvent = useLegacyRootEvent();
    // $bvModal / $router は BootstrapVue / vue-router が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const target = ref<File | null>(null);
    const files = ref<(File | Attachment)[]>([]);
    const deletedFiles = ref<Attachment[]>([]); // 削除するファイル

    const titleSource = ref<string>("");
    const messageSource = ref<string>("");

    // ACL
    const initalAllowWriteAndLikeOtherSource = ref<"ALLOW" | "USERONLY" | "DENY">("USERONLY");
    const allowWriteAndLikeOtherSource = ref<"ALLOW" | "USERONLY" | "DENY">("USERONLY");
    const topicAllowWriteOthersSource = ref<boolean>(true); // 話題の「作成者以外書き込みの許可」
    const aclSource = ref<Acl>(Acl.createDummy());

    // トーク
    const talkIdSource = ref<string>("");
    const sendTalkOnCreateSource = ref<boolean>(false);
    const sendTalkOnUpdateSource = ref<boolean>(false);

    const radioOptions = ref([
      { text: "許可する(ゲストを含む)", value: "ALLOW" },
      { text: "許可する(ゲストを除く)", value: "USERONLY" },
      { text: "許可しない", value: "DENY" },
    ]);

    const replaceParam = ref<{ domainId: string; topicId: string }>({ domainId: "", topicId: "" }); // router.replace用パラメータ

    const prevData = ref<PrevData>({ title: "", message: "", files: [], acl: Acl.createDummy() });
    /* アップローディング関係 */
    const isUploading = ref<boolean>(false);
    const uploadFiles = ref<number>(0);
    const nowuploads = ref<number>(0);

    const submitLock = ref<boolean>(false);

    const drag = ref<boolean>(false);

    // Vuexの変更監視
    let unsubscribeMutation: (() => void) | undefined;

    // $refs.fileSelectionForm
    const fileSelectionForm = ref<FileSelectionForm | null>(null);

    // immediate watch 群は setup 末尾（全 const / computed / 関数定義後）で登録する。
    // immediate watch は watch() 呼び出し時に同期発火するため、後方で定義される const を
    // 参照すると TDZ エラーになる。登録位置を末尾に移して回避。

    // 添付可能か
    const allow_attachment = computed((): boolean => {
      return props.allow_attachment_type != AttachmentFileTypesNone;
    });

    const items = computed((): { label: string; click?: () => void; disabled?: boolean }[] => {
      return [{ label: "保存", click: onEditComplete, disabled: confirmButtonDisabled.value }];
    });

    const navTitle = computed((): string => {
      return props.type == "create" ? "投稿の作成" : "投稿の編集";
    });

    const normalizeMessageId = computed((): string => {
      return props.messageId ? props.messageId : uuidv4();
    });
    const fileListViewerParams = computed((): { messageId: string; commentId: string } => {
      return { messageId: normalizeMessageId.value, commentId: "" };
    });

    const paperclipClass = computed((): string => {
      return allow_attachment.value ? "paperclip" : "paperclip not-allowed";
    });

    const attachmentLabelClass = computed((): Record<string, boolean> => {
      return {
        "attachement-label d-flex flex-row align-items-center": true,
        allow: allow_attachment.value,
        "not-allowed": !allow_attachment.value,
      };
    });

    const titleState = computed((): boolean => {
      if (!titleSource.value) return false;
      const tmp = titleSource.value.trim();
      return getLength(tmp) > 0 && getLength(tmp) <= titleMaxLength();
    });

    const validTitleMessage = computed((): string => {
      const tmp = titleSource.value.trim();
      const titleMaxLengthValue = titleMaxLength();
      return getLength(tmp)
        ? `タイトルは${titleMaxLengthValue}文字以内です(${getLength(tmp)}/${titleMaxLengthValue})`
        : "タイトルは必須項目です";
    });

    const contentState = computed((): boolean => {
      if (!messageSource.value) return false;
      const tmp = messageSource.value.trim().replace(/\r?\n/g, "");
      return getLength(tmp) > 0 && getLength(tmp) <= MESSAGE_CONTENT_MAX_LENGTH;
    });

    const validContentMessage = computed((): string => {
      const tmp = messageSource.value.trim().replace(/\r?\n/g, "");
      return getLength(tmp)
        ? `内容は${MESSAGE_CONTENT_MAX_LENGTH}文字以内です(${getLength(tmp)}/${MESSAGE_CONTENT_MAX_LENGTH})`
        : "内容は必須項目です";
    });

    const confirmButtonDisabled = computed((): boolean => {
      if (AclManager.isGuest(AclManager.store, props.domainId)) return false;
      return !(titleState.value && contentState.value);
    });

    const editMode = computed((): boolean => {
      if (titleSource.value != prevData.value.title) return true;
      if (messageSource.value != prevData.value.message) return true;
      if (JSON.stringify(files.value) != JSON.stringify(prevData.value.files)) return true;
      if (initalAllowWriteAndLikeOtherSource.value != allowWriteAndLikeOtherSource.value)
        return true;
      if (talkIdSource.value != props.talkId) return true;
      if (sendTalkOnCreateSource.value != props.sendTalkOnCreate) return true;
      if (sendTalkOnUpdateSource.value != props.sendTalkOnUpdate) return true;
      return false;
    });

    const loadingMsg = computed((): string => {
      return `添付ファイルを送信中です (${nowuploads.value}/${uploadFiles.value})`;
    });

    // ACL
    /** Topic が createMessageAcl 許可されているかどうかを調べる */
    const createMessageAcl = computed((): boolean => {
      const topicCreateMssageAcl = props.topicAcl?.base?.createMessage;
      return topicCreateMssageAcl != "deny";
    });

    const selectedTalk = computed((): Talk | undefined => {
      if (!talkIdSource.value) return undefined;
      return talks.value.find((t) => t.talk_id_str === talkIdSource.value);
    });

    const showOpenTalkIcon = computed((): boolean => {
      return selectedTalk.value?.show_open_talk_icon === true;
    });

    const talkSelectLabel = computed((): string => {
      if (talkIdSource.value) {
        const talk = selectedTalk.value;
        if (talk) {
          return talk.display_name;
        } else {
          return "所属していないトーク";
        }
      } else {
        return "トークを選択";
      }
    });

    const sendTalkOnCreateDisabled = computed((): boolean => {
      // 編集またはトーク未選択時は無効
      return props.type == "edit" || !talkIdSource.value;
    });

    const sendTalkOnUpdateDisabled = computed((): boolean => {
      // 所属しているトークが選択されている場合は有効
      if (selectedTalk.value) {
        return false;
      } else {
        return true;
      }
    });

    const talks = computed((): Talk[] => {
      return useDomainStore().getTalks as Talk[];
    });

    const hasFeature = (keyword: FeatureName): boolean => {
      return allowFeature(keyword);
    };

    const titleMaxLength = (): number => {
      return hasFeature("topic-title-long") ? 100 : 20; // α/βのキーワードはTopicと同じものを使用
    };

    const dropFiles = (files: FileList): void => {
      const form = fileSelectionForm.value as FileSelectionForm;
      form.dropFiles(files);
    };

    const updateFile = (newFiles: (File | Attachment)[]): void => {
      files.value = newFiles;

      deletedFiles.value = [];
      // 削除予定ファイルを差分から取得
      prevData.value.files.map((_prevFile) => {
        if (AttachmentFactory.isAttachment(_prevFile)) {
          const exist = files.value.find((file) => {
            return AttachmentFactory.isAttachment(file) && file.url === _prevFile.url;
          });
          if (!exist) {
            deletedFiles.value.push(_prevFile);
          }
        }
      });

      changeEditMode(editMode.value);
    };

    const onCancel = (): void => {
      if (props.isModal) {
        emit("cancelToModal");
      } else if (vm?.$router && !props.isModal) {
        vm.$router.back();
      }
    };

    // モーダル用
    const uploadEvent = (): void => {
      emit("uploading", { isUploading: isUploading.value, loadingMsg: loadingMsg.value });
    };

    const onEditComplete = async (): Promise<void> => {
      // 送信連打による連続送信を防止
      if (submitLock.value) return;
      submitLock.value = true;

      // 新規作成前に作成上限を超えている場合はアラート
      if (props.type == "create" && allowCreateMessage(props.domainId, props.topicId) == false) {
        rootEvent.emit("free-alert");
        submitLock.value = false;
        return;
      }

      const msgId = normalizeMessageId.value;
      const form = fileSelectionForm.value as FileSelectionForm;

      const s3AccessUtility = S3AccessUtility.getInstance();
      isUploading.value = true;

      if (deletedFiles.value) {
        // 削除
        await s3AccessUtility.deleteFiles(deletedFiles.value, {
          domainId: props.domainId,
          topicId: props.topicId,
          messageId: msgId,
        });
        deletedFiles.value = [];
      }

      let files_: Attachment[] = [];
      const generator = async () => {
        let returnValue: boolean = true;
        for await (const result of s3AccessUtility.uploadFilesGenerator(files.value, {
          domainId: props.domainId,
          topicId: props.topicId,
          messageId: msgId,
        })) {
          if (!result) {
            returnValue = false;
            break;
          }

          files_ = result.attachments;
          nowuploads.value = result.uploaded;
          uploadFiles.value = result.total;
          if (result.done) {
            returnValue = true;
            break;
          }
        }
        return returnValue;
      };

      if (props.isModal) uploadEvent();
      // アップロード
      const result = await generator();
      if (!result) {
        rootEvent.emit("show-error-modal", {
          msg: "添付ファイルの送信に失敗しました。",
          afterProcess: () => {
            return;
          },
        });
        return;
      }
      isUploading.value = false;
      if (props.isModal) uploadEvent();
      nowuploads.value = 0;
      form.resetFileList();

      if (!files_) return;
      const message = Message.create({
        id: msgId,
        domainId: props.domainId,
        topicId: props.topicId,
        title: titleSource.value,
        message: messageSource.value,
        photos: files_,
        acl: Acl.createByMessage(msgId, allowWriteAndLikeOtherSource.value),
        talkId: talkIdSource.value,
        sendTalkOnCreate: sendTalkOnCreateSource.value,
        sendTalkOnUpdate: sendTalkOnUpdateSource.value,
      });
      const param: Partial<Message> = {
        title: titleSource.value,
        message: messageSource.value,
        photos: files_,
        acl: Acl.createByMessage(msgId, allowWriteAndLikeOtherSource.value),
        talkId: talkIdSource.value,
        sendTalkOnCreate: sendTalkOnCreateSource.value,
        sendTalkOnUpdate: sendTalkOnUpdateSource.value,
      };
      const obj = props.type == "edit" ? { messageId: msgId, param: param } : { message: message };
      const event = props.type == "edit" ? "on-message-edit" : "on-message-create";
      if (vm?.$root && vm.$root.$emit) {
        rootEvent.emit(event, obj);
      }
      emit(event, obj);

      changeEditMode(false);

      if (props.isModal) {
        emit("confirmToModal");
      } else if (vm?.$router && !props.isModal) {
        vm.$router.back(); // 完了後は閉じる
      }
      submitLock.value = false;
    };

    // 削除後のイベント
    const afterDeleteProcess = (deletedType: string): void => {
      switch (deletedType) {
        case "topic":
          if (props.isModal) {
            console.log("domainId", replaceParam.value.domainId);
            // modal close => $router.replace
            emit("interruption", {
              id: "modal-message",
              path: `/${replaceParam.value.domainId}`,
            });
          } else {
            vm?.$router.replace(`/${replaceParam.value.domainId}`);
          }
          break;
        case "message":
          if (props.isModal) {
            // modal close
            emit("interruption", { id: "modal-message", path: "" });
          } else {
            vm?.$router.replace(`/${replaceParam.value.domainId}/${replaceParam.value.topicId}`);
          }
          break;
        default:
          break;
      }
    };

    const showTalkSelectModal = (): void => {
      if (talkIdSource.value && !selectedTalk.value) {
        // 所属していないトークが選択されている場合は確認メッセージを表示
        vm?.$bvModal.show("confirm-not-joined-talk");
      } else {
        vm?.$bvModal.show("talk-select-modal");
      }
    };

    const confirmedNotJoinedTalk = (): void => {
      vm?.$bvModal.show("talk-select-modal");
    };

    const talkSelected = (talk: Partial<Talk>): void => {
      const talkId = talk.talk_id_str || "";

      // 作成時に未選択状態からトークが選択された場合
      if (props.type == "create" && !talkIdSource.value && talkId) {
        // 「作成時に送信」にチェックを入れる
        sendTalkOnCreateSource.value = true;
      }
      // 「なし」が選択された場合
      else if (!talkId) {
        // 新規の場合は「作成時に送信」のチェックを外す
        if (props.type == "create") {
          sendTalkOnCreateSource.value = false;
        }
        // 「編集時に送信」のチェックを外す
        sendTalkOnUpdateSource.value = false;
      }

      talkIdSource.value = talkId;

      changeEditMode(editMode.value);
    };

    // Lifecycle
    onBeforeUnmount(() => {
      if (unsubscribeMutation) unsubscribeMutation();
    });

    // created 相当（props の一回スナップショット。リアクティブ追跡は不要）
    onBeforeMount(() => {
      if (props.photos.length) {
        files.value = props.photos.slice();
      }
      prevData.value = {
        title: props.title,
        message: props.message,
        files: props.photos,
        acl: props.acl,
      };
    });

    onMounted(() => {
      // 旧 Vuex の topics/delete・messages/add(削除) mutation 監視を state 変化監視（$subscribe）へ移植。
      // 当該 mutation は action / GraphQL サブスクリプション双方から発火していたため $onAction では捕捉しきれない。
      const topicStore = useTopicStore();
      const messageStore = useMessageStore();
      let topicWasPresent = !!topicStore.getOne(props.domainId, props.topicId);
      const isOpenMessageDeleted = (): boolean => {
        const messageId = normalizeMessageId.value;
        const list = messageStore.get(props.domainId, props.topicId) || [];
        const msg = list.find((m) => m.id == messageId);
        return !!msg && msg.deleted == true;
      };
      let messageWasDeleted = isOpenMessageDeleted();

      // 投稿「新規作成中／編集中」に「話題」削除 -> 「話題一覧」に移動
      const unsubTopic = topicStore.$subscribe(() => {
        const present = !!topicStore.getOne(props.domainId, props.topicId);
        if (topicWasPresent && !present) {
          const domainId = props.domainId;
          const topicId = props.topicId;
          replaceParam.value = { domainId: domainId, topicId: topicId };
          rootEvent.emit("show-error-modal", {
            msg: "話題が削除されたため、話題一覧に移動します",
            afterProcess: () => afterDeleteProcess("topic"),
          });
        }
        topicWasPresent = present;
      });
      // 投稿「編集中」に「投稿」削除 -> 「投稿一覧」に移動
      const unsubMessage = messageStore.$subscribe(() => {
        const deleted = isOpenMessageDeleted();
        if (!messageWasDeleted && deleted) {
          const domainId = props.domainId;
          const topicId = props.topicId;
          replaceParam.value = { domainId: domainId, topicId: topicId };
          rootEvent.emit("show-error-modal", {
            msg: "投稿が削除されたため、投稿一覧に移動します",
            afterProcess: () => afterDeleteProcess("message"),
          });
        }
        messageWasDeleted = deleted;
      });
      unsubscribeMutation = () => {
        unsubTopic();
        unsubMessage();
      };

      // トークを取得
      useAppStore().fetchDirectTalks(props.domainId);
    });

    // immediate watch 群（全 const / computed / 関数定義後に登録: TDZ 回避）
    watch(
      () => props.title,
      (value: string): void => {
        titleSource.value = value;
      },
      { immediate: true }
    );

    watch(
      () => props.message,
      (value: string): void => {
        messageSource.value = value;
      },
      { immediate: true }
    );

    watch(
      () => props.photos,
      (value: Attachment[]): void => {
        files.value = value.slice();
      },
      { immediate: true }
    );

    watch(
      () => props.acl,
      (value: Acl): void => {
        aclSource.value = value;
        const createCommentAcl = value.base.createComment;
        const parentTopic = useTopicStore().getOne(props.domainId, props.topicId) as Topic;
        topicAllowWriteOthersSource.value = parentTopic.acl.createMessageByOthers();
        if (!topicAllowWriteOthersSource.value) {
          allowWriteAndLikeOtherSource.value = "DENY";
          initalAllowWriteAndLikeOtherSource.value = allowWriteAndLikeOtherSource.value;
          return;
        }
        const allowWriteAndLike = createCommentAcl != "deny";
        const allowGuest = value.guest.create;
        if (allowWriteAndLike && allowGuest) {
          allowWriteAndLikeOtherSource.value = "ALLOW";
        } else if (allowWriteAndLike && !allowGuest) {
          allowWriteAndLikeOtherSource.value = "USERONLY";
        } else {
          allowWriteAndLikeOtherSource.value = "DENY";
        }
        initalAllowWriteAndLikeOtherSource.value = allowWriteAndLikeOtherSource.value;
      },
      { immediate: true }
    );

    watch(
      () => props.talkId,
      (value: string): void => {
        talkIdSource.value = value;
      },
      { immediate: true }
    );

    watch(
      () => props.sendTalkOnCreate,
      (value: boolean): void => {
        sendTalkOnCreateSource.value = value;
      },
      { immediate: true }
    );

    watch(
      () => props.sendTalkOnUpdate,
      (value: boolean): void => {
        sendTalkOnUpdateSource.value = value;
      },
      { immediate: true }
    );

    return {
      target,
      files,
      deletedFiles,
      titleSource,
      messageSource,
      initalAllowWriteAndLikeOtherSource,
      allowWriteAndLikeOtherSource,
      topicAllowWriteOthersSource,
      aclSource,
      talkIdSource,
      sendTalkOnCreateSource,
      sendTalkOnUpdateSource,
      radioOptions,
      replaceParam,
      prevData,
      isUploading,
      uploadFiles,
      nowuploads,
      submitLock,
      drag,
      fileSelectionForm,
      changeEditMode,
      showEditDiscardAlert,
      routeLeaveEvent,
      allow_attachment,
      items,
      navTitle,
      normalizeMessageId,
      fileListViewerParams,
      paperclipClass,
      attachmentLabelClass,
      titleState,
      validTitleMessage,
      contentState,
      validContentMessage,
      confirmButtonDisabled,
      editMode,
      loadingMsg,
      createMessageAcl,
      selectedTalk,
      showOpenTalkIcon,
      talkSelectLabel,
      sendTalkOnCreateDisabled,
      sendTalkOnUpdateDisabled,
      talks,
      hasFeature,
      titleMaxLength,
      dropFiles,
      updateFile,
      onCancel,
      uploadEvent,
      onEditComplete,
      afterDeleteProcess,
      showTalkSelectModal,
      confirmedNotJoinedTalk,
      talkSelected,
    };
  },
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext): void {
    // 編集画面でリロードされた場合、投稿ページへリダイレクト
    if (from.name) {
      next();
    } else {
      const domainId = to.params.domainId;
      const topicId = to.params.topicId;
      console.log(`* redirect: /${domainId}/${topicId}`);
      next(`/${domainId}/${topicId}`);
    }
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): void {
    this.routeLeaveEvent(next);
  },
});
</script>
